<template>
  <div class="w-full flex items-center gap-x-2 gap-y-1.5 flex-wrap py-1">
    <!-- Preset Filters -->
    <PresetFiltersItem 
      v-if="activePreset" 
      :presetInfo="activePreset.presetInfo" 
      :filters="activePreset.filters" 
      @removePreset="clearFilters" 
      @removeFilter="removeFilter"
    />
    <!-- Included Filters -->
    <IncludedFiltersIcon v-if="(includedFilterItems.length || componentFilterItems.length) && excludedFilterItems.length" />
    <FilterItem 
      v-for="filter in includedFilterItems"
      :key="`included-${filter.key}`"
      :category="filter.category"
      :values="filter.values"
      :active-filters="activeFilters"
      @applyFilters="$emit('applyFilters', $event)"
      @remove="removeFilterItem(filter.key)"
    />
    <ComponentFilterItem 
      v-for="filter in componentFilterItems"
      :key="`component-${filter.key}`"
      :category="filter.category"
      :selection="filter.selection"
      :active-filters="activeFilters"
      @applyFilters="$emit('applyFilters', $event)"
      @remove="removeComponentFilterItem(filter.key)"
    />
    <!-- Excluded Filters -->
    <div v-for="(filter, index) in excludedFilterItems" :key="`excluded-${filter.key}`" class="flex items-center gap-2 flex-nowrap">
      <ExcludedFiltersIcon v-if="index === 0" />
      <FilterItem 
        :category="filter.category"
        :values="filter.values"
        :active-filters="activeFilters"
        @applyFilters="$emit('applyFilters', $event)"
        @remove="removeFilterItem(filter.key)"
      />
    </div>
    
    <!-- Actions -->
    <div class="flex items-center gap-1">
      <!-- Save Filters Button -->
      <div v-if="showSaveFilters" class="relative">
        <button class="p-1 rounded-md transition-colors duration-100 text-text-disabled hover:text-icon-muted hover:bg-neutral-25"
        @mouseenter="actionHovered('save')" @mouseleave="actionUnhovered('save')" @click="$emit('createPreset')">
          <SaveFiltersIcon />
        </button>
        <transition>
          <div v-if="tooltipVisible.save" class="tooltip px-2 py-1 rounded-md shadow-lg w-max">
            <BaseText type="label" size="xs" class="text-white">
              Save Filters
            </BaseText>
          </div>
        </transition>
      </div>
      <!-- Clear Filters Button -->
      <div v-if="showClearFilters" class="relative">
        <button class="group p-1 rounded-md transition-colors duration-100 hover:bg-neutral-25"
        @mouseenter="actionHovered('clear')" @mouseleave="actionUnhovered('clear')" @click="clearFilters">
          <ClearFiltersIcon className="text-text-disabled transition-colors duration-100 group-hover:text-icon-muted" />
        </button>
        <transition>
          <div v-if="tooltipVisible.clear" class="tooltip px-2 py-1 rounded-md shadow-lg w-max">
            <BaseText type="label" size="xs" class="text-white">
              Clear Filters
            </BaseText>
          </div>
        </transition>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

// Components
import FilterItem from './list/FilterItem.vue'
import ComponentFilterItem from './list/ComponentFilterItem.vue'
import PresetFiltersItem from './list/PresetFiltersItem.vue'

// Icons
import IncludedFiltersIcon from '../globals/Icons/FilterIcons/IncludedFiltersIcon.vue'
import ExcludedFiltersIcon from '../globals/Icons/FilterIcons/ExcludedFiltersIcon.vue'
import SaveFiltersIcon from '../globals/Icons/FilterIcons/SaveFiltersIcon.vue'
import ClearFiltersIcon from '../globals/Icons/FilterIcons/ClearFiltersIcon.vue'
import { _ } from 'core-js'

export default {
  name: 'ActiveFiltersList',
  components: {
    FilterItem,
    ComponentFilterItem,
    PresetFiltersItem,
    IncludedFiltersIcon,
    ExcludedFiltersIcon,
    SaveFiltersIcon,
    ClearFiltersIcon
  },
  props: {
    activeFilters: {
      type: Object,
      required: true
    },
    isPresetEditor: {
      type: Boolean,
      default: false
    },
    hideSaveOption: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      tooltipVisible: {
        save: false,
        clear: false
      },
      tooltipTimeout: {
        save: null,
        clear: null
      }
    }
  },
  computed: {
    ...mapGetters('SearchModule', ['getFilterCategories']),
    activePreset () {
      if (!this.activeFilters.appliedPreset) return null
      const { appliedPreset, ...filters } = this.activeFilters
      return {
        presetInfo: appliedPreset,
        filters
      }
    },
    includedFilterItems () {
      if (this.activeFilters.appliedPreset) return []
      const { componentFilters, ...basicFilters } = this.activeFilters
      const filterEntries = Object.entries(basicFilters).filter(([key, _]) => {
        const logicalOperator = key.split('.')[0]
        return logicalOperator !== 'exclude'
      })
      return this.mapEntriesToFilterItems(filterEntries)
    },
    componentFilterItems () {
      if (this.activeFilters.appliedPreset || !this.activeFilters.componentFilters) return []
      const { componentFilters } = this.activeFilters
      return Object.entries(componentFilters).map(([key, value]) => {
        const category = this.getFilterCategories.find(category => category.id === key)
        return {
          key,
          category,
          selection: value
        }
      })
    },
    excludedFilterItems () {
      if (this.activeFilters.appliedPreset) return []
      const { componentFilters, ...basicFilters } = this.activeFilters
      const filterEntries = Object.entries(basicFilters).filter(([key, _]) => {
        const logicalOperator = key.split('.')[0]
        return logicalOperator === 'exclude'
      })
      return this.mapEntriesToFilterItems(filterEntries)
    },
    showClearFilters () {
      return this.includedFilterItems.length + this.excludedFilterItems.length + this.componentFilterItems.length > 1 && !this.isPresetEditor
    },
    showSaveFilters () {
      return !this.hideSaveOption && this.includedFilterItems.length + this.excludedFilterItems.length + this.componentFilterItems.length > 0 && !this.isPresetEditor
    }
  },
  methods: {
    removeFilter (payload) {
      const { key, value, isComponent } = payload
      const updatedFilters = _.cloneDeep(this.activeFilters)

      if (isComponent) {
        delete updatedFilters.componentFilters[key]
        if (_.isEmpty(updatedFilters.componentFilters)) delete updatedFilters.componentFilters
      } else {
        updatedFilters[key] = updatedFilters[key].filter(v => v !== value)
        if (!updatedFilters[key].length) delete updatedFilters[key]
      }
      delete updatedFilters.appliedPreset
      this.$emit('applyFilters', updatedFilters)
    },
    removeFilterItem (key) {
      const updatedFilters = _.cloneDeep(this.activeFilters)
      delete updatedFilters[key]
      delete updatedFilters.appliedPreset
      this.$emit('applyFilters', updatedFilters)
    },
    removeComponentFilterItem (key) {
      const updatedFilters = _.cloneDeep(this.activeFilters)
      delete updatedFilters.componentFilters[key]
      if (_.isEmpty(updatedFilters.componentFilters)) delete updatedFilters.componentFilters
      delete updatedFilters.appliedPreset
      this.$emit('applyFilters', updatedFilters)
    },
    clearFilters () {
      this.$emit('applyFilters', {})
      this.actionUnhovered('clear')
    },
    mapEntriesToFilterItems (filterEntries) {
      return filterEntries.map(([key, valueArray]) => {
        const categoryId = key.split('.')[1]
        const category = this.getFilterCategories.find(category => category.id === categoryId)
        return {
          key,
          category,
          values: valueArray
        }
      })
    },
    actionHovered (type) {
      clearTimeout(this.tooltipTimeout[type])
      this.tooltipTimeout[type] = setTimeout(() => {
        this.tooltipVisible[type] = true
      }, 1000)
    },
    actionUnhovered (type) {
      clearTimeout(this.tooltipTimeout[type])
      this.tooltipVisible[type] = false
    }
  }
}
</script>

<style scoped>
.tooltip {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 50%;
  transform: translateX(-50%);
  background-color: #100f1b;
}

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
</style>